import React from "react"
import { Link } from "gatsby"
import { graphql } from "gatsby"
import Image from "gatsby-image"

import { rhythm } from "../utils/typography"
import Layout from "../components/layout"
import SEO from "../components/seo"

const Index = ({ data, location }) => {
  const siteTitle = data.site.siteMetadata.title
  const companyLogoSources = [
    data.companyLogosMobile.childImageSharp.fluid,
    {
      ...data.companyLogosDesktop.childImageSharp.fluid,
      media: `(min-width: 768px)`,
    },
  ]

  return (
    <Layout location={location} title={siteTitle}>
      <SEO title="Welcome" />
      <Image
        fluid={data.mast.childImageSharp.fluid}
        alt="Me"
        style={{
          maxWidth: `100%`,
          marginBottom: rhythm(1 / 2),
          marginLeft: `auto`,
          marginRight: `auto`,
          marginTop: 0,
          maxHeight: `75vh`,
        }}
      />
      <div style={{ padding: "0 1rem" }}>
        <h1
          style={{
            textAlign: `center`,
            margin: `4rem 0 0.5rem`,
            textTransform: "none",
          }}
        >
          Story-led films for independent brands and organisations
        </h1>
        <h2
          style={{
            textAlign: `center`,
            margin: `0 0 1rem`,
            textTransform: "none",
          }}
        >
          End-to-end video production that captures people, places and purpose
        </h2>
      </div>
      <div
        style={{
          maxWidth: rhythm(44),
          marginLeft: `auto`,
          marginRight: `auto`,
          padding: `${rhythm(1.5)} ${rhythm(3 / 4)} ${rhythm(0)}`,
        }}
      >
        <div className="video-wrapper" style={{ marginBottom: `2rem` }}>
          <iframe
            src="https://player.vimeo.com/video/208821024?autoplay=0"
            width="100%"
            height="316"
            title="Crafted Films"
          ></iframe>
        </div>
      </div>

      <div style={{ margin: `auto`, maxWidth: rhythm(34) }}>
        <div className="grid align-center">
          <div className="col col-6">
            <h2 style={{ marginTop: 0 }}>What we do</h2>
            <p>
              Crafted Films leads strategic video projects from concept through
              to delivery.
            </p>
            <p>
              From brand films and campaign pieces to documentary-style case
              studies, each production is carefully shaped to reflect the depth,
              personality and thinking behind your business - not just what you
              sell.
            </p>
            <p>
              Working with a trusted network of specialist collaborators,
              Crafted Films scales each project according to its needs, ensuring
              every production is tailored, efficient and thoughtfully executed.
            </p>
          </div>
          <div className="col col-6">
            <Image
              fluid={data.whatWeDo.childImageSharp.fluid}
              alt="What we do"
              style={{
                maxWidth: `100%`,
                margin: "auto",
              }}
            />
          </div>
        </div>

        <div className="grid align-center">
          <div className="col col-6">
            <Image
              fluid={data.philosophy.childImageSharp.fluid}
              alt="Philosophy"
              style={{
                maxWidth: `100%`,
                margin: "auto",
              }}
            />
          </div>
          <div className="col col-6">
            <h2 style={{ marginTop: 0 }}>Philosophy</h2>
            <p>
              In a world of constant content, clarity matters more than volume.
            </p>
            <p>
              The most effective films aren't created for a moment, they're
              shaped to support the business over time.
            </p>
            <p>
              Rather than chasing trends, I focus on creating considered video
              assets that sit at the heart of your marketing, anchoring your
              website, supporting launches and strengthening brand perception
              long after the initial campaign.
            </p>
          </div>
        </div>

        <div className="grid align-center">
          <div className="col col-6">
            <h2 style={{ marginTop: 0 }}>Process</h2>
            <p>Every project begins with understanding the bigger picture.</p>
            <p>
              What are you trying to achieve? <br />
              Who are you speaking to? <br />
              How should your audience feel?
            </p>
            <p>
              From there, I shape the narrative, lead production and oversee
              delivery, ensuring the process feels calm, structured and
              collaborative from start to finish.
            </p>
          </div>
          <div className="col col-6">
            <Image
              fluid={data.process.childImageSharp.fluid}
              alt="Process"
              style={{
                maxWidth: `320px`,
                margin: "auto",
              }}
            />
          </div>
        </div>

        <div className="grid align-center">
          <div className="col col-6">
            <Image
              fluid={data.letsTalk.childImageSharp.fluid}
              alt="Let's talk"
              style={{
                maxWidth: `240px`,
                margin: "auto",
              }}
            />
          </div>
          <div className="col col-6">
            <h2 style={{ marginTop: 0 }}>Let's talk</h2>
            <p>
              If you're ready to use video more strategically within your
              business, I'd love to hear what you're working on.
            </p>
            <p>
              Every project begins with a conversation - understanding your
              objectives, your audience and how film can best support your wider
              goals. From there, we can explore the right approach together.
            </p>
          </div>
        </div>
      </div>

      <div style={{ margin: `auto`, maxWidth: rhythm(34) }}>
        <div style={{ textAlign: `center`, margin: `4rem 0 0` }}>
          <Link to={`/portfolio`} className="button">
            Portfolio
          </Link>
        </div>
        <section>
          <h2 style={{ textAlign: `center`, margin: `4rem 0 2rem` }}>
            Trusted by leading brands
          </h2>
          <Image
            fluid={companyLogoSources}
            alt="Client company logos"
            style={{
              position: "relative",
              height: "100%",
              width: "100%",
              marginBottom: rhythm(1),
              marginLeft: `auto`,
              marginRight: `auto`,
              marginTop: 0,
            }}
          />
        </section>
      </div>
    </Layout>
  )
}

export default Index

export const pageQuery = graphql`
  query {
    mast: file(absolutePath: { regex: "/mast-collage.jpg/" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 100) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    companyLogosMobile: file(
      absolutePath: { regex: "/crafted-films-company-logos-mobile.jpg/" }
    ) {
      childImageSharp {
        fluid(maxWidth: 700, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    companyLogosDesktop: file(
      absolutePath: { regex: "/crafted-films-company-logos-desktop.jpg/" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    irisCamera: file(absolutePath: { regex: "/iris-camera.jpg/" }) {
      childImageSharp {
        fluid(maxWidth: 660, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    girlsHappy: file(absolutePath: { regex: "/girls-happy.jpg/" }) {
      childImageSharp {
        fluid(maxWidth: 660, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    whatWeDo: file(absolutePath: { regex: "/film-footage-phone-view.jpg/" }) {
      childImageSharp {
        fluid(maxWidth: 660, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    philosophy: file(absolutePath: { regex: "/philosophy.jpg/" }) {
      childImageSharp {
        fluid(maxWidth: 660, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    process: file(absolutePath: { regex: "/process.png/" }) {
      childImageSharp {
        fluid(maxWidth: 660, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    letsTalk: file(absolutePath: { regex: "/iris-lets-talk.jpg/" }) {
      childImageSharp {
        fluid(maxWidth: 660, quality: 80) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`
